import { useWebSiteStore } from '@/stores/web-site.store'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useNotify } from '@/composables/notify'
import { convertCurrencyAmount, documentExchangeRate } from '@/composables/exchange-rate'
import { getI18n } from '@/plugins/i18n'
import { fixedDecimal } from '@/lib/utils'
import { getAmountDecimals } from '@/composables/currency'

const PERCENTAGE_DECIMALS = 6
const UNIT_PRICE_DECIMALS = 6

export const usePriceService = (options) => {
  const accountingStore = useAccountingStore()
  const planningStore = usePlanningStore()
  const webSiteStore = useWebSiteStore()
  const { notifyWarning } = useNotify()
  const i18n = getI18n()

  const getListingItemAmounts = (listingItem) => {
    const budgetDocument = options?.budgetDocument
    const convert = (amount) => convertCurrencyAmount(amount, listingItem.currency_ref, budgetDocument?.currency_ref, documentExchangeRate(budgetDocument))
    const amounts = { price: convert(listingItem.price), cost: convert(listingItem.cost) }
    if ((listingItem.price != null && amounts.price == null) || (listingItem.cost != null && amounts.cost == null)) {
      notifyWarning(i18n.t('error.validation.exchangeRateMissing'))
    }
    return amounts
  }

  const getCurrentPriceValue = (data) => {
    return data.cost_valorization === accountingStore.costValorizationsEnum.COST ? data.cost : data.price
  }

  const isUomTimeBased = (unitOfMeasureRef) => {
    if (!unitOfMeasureRef) return false
    const uom = webSiteStore.getLocalizedUnitsOfMeasure().find(u => u.id === unitOfMeasureRef)
    return uom?.is_time_based ?? false
  }

  const getUnroundedTotalNoVatValue = (data) => {
    if (!getCurrentPriceValue(data) || !data.quantity) {
      return 0
    }
    let total = data.quantity * getCurrentPriceValue(data)
    if (data.charge_percentage) {
      total = total * (1 + data.charge_percentage)
    }
    if (data.discount) {
      total = total * (1 - data.discount)
    }
    if (data.discount_2) {
      total = total * (1 - data.discount_2)
    }
    if (data.discount_3) {
      total = total * (1 - data.discount_3)
    }
    if (data.urgency_discount) {
      total = total * (1 - data.urgency_discount)
    }
    if (data.urgency_discount_2) {
      total = total * (1 - data.urgency_discount_2)
    }
    if (isUomTimeBased(data.unit_of_measure_ref)) {
      total = total * (data.amount_per_unit ?? 1)
    }
    return total
  }

  const getTotalNoVatValue = (data) => fixedDecimal(getUnroundedTotalNoVatValue(data), getAmountDecimals())

  const getVatPercentage = (data) => Number(data.vat_type_ref
    ? (data.vat_type_percentage ?? getVatById(data.vat_type_ref)?.percentage ?? 0)
    : (data.vat_type_percentage ?? 0))

  const getTotalCostValue = (data) => {
    if (!data.price || !data.quantity) {
      return 0
    }
    return data.quantity * data.cost
  }

  const getTotalVatValue = (data) => fixedDecimal(getTotalNoVatValue(data) * (1 + getVatPercentage(data)), getAmountDecimals())

  const getVatById = (id) => {
    return webSiteStore.vatTypes.find(i => i.id === id)
  }

  const setDiscountCategoryValue = (newData, value, currentRowData, skipTotalUpdate = false) => {
    newData.discount_category_ref = value
    const discountCategory = registryStore.discountCategories.find(i => i.id === value)
    if (discountCategory) {
      newData.discount = discountCategory?.discount
      newData.discount_2 = discountCategory?.discount_2
      newData.discount_3 = discountCategory?.discount_3
    } else {
      newData.discount = newData.discount_2 = newData.discount_3 = null
    }

    if (!skipTotalUpdate) {
      setTotalNoVatValue(newData, currentRowData)
      setTotalVatValue(newData, currentRowData)
    }
  }

  const setCostValorizationValue = async(newData, value, currentRowData) => {
    newData.cost_valorization = value

    const currentListingItem = planningStore.listingItems.find(i => i.id === currentRowData.listing_item)
    if (currentListingItem) {
      const listingAmounts = getListingItemAmounts(currentListingItem)
      let currentPrice = value === accountingStore.costValorizationsEnum.COST ? listingAmounts.cost : listingAmounts.price
      if (!currentPrice) {
        notifyWarning(i18n.t(value === accountingStore.costValorizationsEnum.COST ? 'warning.costValorizationEmpty.cost' : 'warning.costValorizationEmpty.price'))
        currentPrice = 0
      }
      if (value === accountingStore.costValorizationsEnum.COST) {
        setCostValue(newData, currentPrice, currentRowData, true)
      } else {
        setPriceValue(newData, currentPrice, currentRowData, true)
      }
    }
    setTotalNoVatValue(newData, currentRowData)
    setTotalVatValue(newData, currentRowData)
  }

  const setPriceValue = (newData, value, currentRowData, skipTotalUpdate = false) => {
    newData.price = value
    if (newData.price < (newData.cost || currentRowData.cost)) {
      notifyWarning(i18n.t('warning.priceLowerThanCost'))
    }
    if (!skipTotalUpdate) {
      setTotalNoVatValue(newData, currentRowData)
      setTotalVatValue(newData, currentRowData)
    }
  }

  const setCostValue = (newData, value, currentRowData, skipTotalUpdate = false) => {
    newData.cost = value
    if (!skipTotalUpdate) {
      setTotalCostValue(newData, currentRowData)
      setTotalNoVatValue(newData, currentRowData)
      setTotalVatValue(newData, currentRowData)
    }
  }

  const setQuantityValue = (newData, value, currentRowData, skipTotalUpdate = false) => {
    newData.quantity = value
    if (!skipTotalUpdate) {
      setTotalNoVatValue(newData, currentRowData)
      setTotalVatValue(newData, currentRowData)
      setTotalCostValue(newData, currentRowData)
    }
  }

  const setVatTypeValue = (newData, value, currentRowData, skipTotalUpdate = false) => {
    newData.vat_type_ref = value
    newData.vat_type_percentage = null
    if (value) {
      newData.vat_type_percentage = webSiteStore.vatTypes.find(i => i.id === value)?.percentage
    }
    if (!skipTotalUpdate) {
      setTotalNoVatValue(newData, currentRowData)
      setTotalVatValue(newData, currentRowData)
    }
  }

  const setTotalNoVatValue = (newData, currentRowData) => {
    newData.total_no_vat = getTotalNoVatValue({ ...currentRowData, ...newData })
  }

  const setTotalVatValue = (newData, currentRowData) => {
    newData.total_vat = getTotalVatValue({ ...currentRowData, ...newData })
  }

  // Reverse chain: total with VAT → total without VAT → unit price/cost. The unit multiplier
  // is obtained by running the forward formula with a unit price, so quantity, charge,
  // discounts and amount per unit are never re-implemented here — it mirrors getTotalNoVatValue.
  const setTotalNoVatEnteredValue = (newData, value, currentRowData) => {
    const data = { ...currentRowData, ...newData }
    const unitTotal = getUnroundedTotalNoVatValue({ ...data, price: 1, cost: 1 })
    const unitValue = unitTotal ? fixedDecimal(fixedDecimal(value, getAmountDecimals()) / unitTotal, UNIT_PRICE_DECIMALS) : 0
    if (data.cost_valorization === accountingStore.costValorizationsEnum.COST) {
      newData.cost = unitValue
      setTotalCostValue(newData, currentRowData)
    } else {
      newData.price = unitValue
    }
    setTotalNoVatValue(newData, currentRowData)
    setTotalVatValue(newData, currentRowData)
  }

  // The total with VAT is derived from the rounded total without VAT, so the entered value may be re-updated by a cent.
  const setTotalVatEnteredValue = (newData, value, currentRowData) => {
    setTotalNoVatEnteredValue(newData, value / (1 + getVatPercentage({ ...currentRowData, ...newData })), currentRowData)
  }

  // Discount and charge are mutually exclusive: setting one clears the other.
  const setDiscountValue = (newData, value, currentRowData) => {
    newData.discount = value == null || value === '' ? null : fixedDecimal(value, PERCENTAGE_DECIMALS)
    if (newData.discount) newData.charge_percentage = null
    setTotalNoVatValue(newData, currentRowData)
    setTotalVatValue(newData, currentRowData)
  }

  const setChargePercentageValue = (newData, value, currentRowData) => {
    newData.charge_percentage = value == null || value === '' ? null : fixedDecimal(value, PERCENTAGE_DECIMALS)
    if (newData.charge_percentage) newData.discount = null
    setTotalNoVatValue(newData, currentRowData)
    setTotalVatValue(newData, currentRowData)
  }

  // A total below the undiscounted one becomes a discount, above it a charge: never a negative discount.
  // With no undiscounted total to scale (zero price), the total sets the unit price instead.
  const setTotalNoVatAdjustmentValue = (newData, value, currentRowData) => {
    const data = { ...currentRowData, ...newData }
    const base = getUnroundedTotalNoVatValue({ ...data, discount: null, charge_percentage: null })
    if (!base) {
      newData.discount = newData.charge_percentage = null
      setTotalNoVatEnteredValue(newData, value, currentRowData)
      return
    }
    const ratio = fixedDecimal(value, getAmountDecimals()) / base
    newData.discount = ratio < 1 ? fixedDecimal(1 - ratio, PERCENTAGE_DECIMALS) : null
    newData.charge_percentage = ratio > 1 ? fixedDecimal(ratio - 1, PERCENTAGE_DECIMALS) : null
    setTotalNoVatValue(newData, currentRowData)
    setTotalVatValue(newData, currentRowData)
  }

  const setTotalVatAdjustmentValue = (newData, value, currentRowData) => {
    setTotalNoVatAdjustmentValue(newData, value / (1 + getVatPercentage({ ...currentRowData, ...newData })), currentRowData)
  }

  const setTotalCostValue = (newData, currentRowData) => {
    newData.total_cost = getTotalCostValue({ ...currentRowData, ...newData })
  }

  const setDocumentRowTypeValue = (newData, value, currentRowData) => {
    newData.document_row_type = value
    newData.listing_item = newData.listing_item_text = null
    newData.price = 0
    newData.quantity = 1
    newData.total_no_vat = 0
    newData.total_vat = 0
    newData.product_text = null
    newData.discount = newData.discount_2 = newData.discount_3 = null
    newData.description = null
    newData.budget_document_row_category_ref = null
    newData.vat_type_ref = null
    newData.vat_type_percentage = null
    if (value === webSiteStore.documentRowTypeEnum.COMMENT) {
      newData.quantity = newData.price = 0
      newData.unit_of_measure_ref = null
    }
  }

  const setListingItemValue = (newData, value, currentRowData, skipTotalUpdate = false) => {
    if (!currentRowData.document_row_type || currentRowData.document_row_type === webSiteStore.documentRowTypeEnum.LISTING_ITEM) {
      newData.listing_item = value.id
    } else {
      newData.listing_item = null
    }
    newData.listing_item_text = value.name
    newData.description = value.name
    const listingAmounts = getListingItemAmounts(value)
    newData.price = currentRowData.cost_valorization === accountingStore.costValorizationsEnum.COST ? listingAmounts.cost : listingAmounts.price
    newData.cost = listingAmounts.cost
    newData.unit_of_measure_ref = value.unit_of_measure_ref
    newData.amount_per_unit = isUomTimeBased(value.unit_of_measure_ref) ? 1 : null
    setVatTypeValue(newData, value.vat_type_ref, currentRowData)
    newData.quantity = currentRowData.quantity || 1

    if (!skipTotalUpdate) {
      setTotalNoVatValue(newData, currentRowData)
      setTotalVatValue(newData, currentRowData)
      setTotalCostValue(newData, currentRowData)
    }
  }

  return {
    getListingItemAmounts,
    getTotalNoVatValue,
    getTotalVatValue,
    isUomTimeBased,
    setDiscountValue,
    setChargePercentageValue,
    setTotalNoVatAdjustmentValue,
    setTotalVatAdjustmentValue,
    setCostValorizationValue,
    setDiscountCategoryValue,
    setDocumentRowTypeValue,
    setPriceValue,
    setCostValue,
    setListingItemValue,
    setQuantityValue,
    setTotalNoVatValue,
    setTotalNoVatEnteredValue,
    setTotalVatValue,
    setTotalVatEnteredValue,
    setVatTypeValue,
  }
}
